import React, { useState } from 'react';
import { Thermometer, Droplet, Zap, Building, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { CoolingMetrics, StormwaterMetrics, formatCurrency, formatIndianNumber } from '../../utils/environmentalCalculations';
import { NatureCard } from '../shared/NatureCard';
import { MetricDisplay } from '../shared/MetricDisplay';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './AdditionalBenefits.css';

interface AdditionalBenefitsProps {
  cooling: CoolingMetrics;
  stormwater: StormwaterMetrics;
  onShowReferences?: () => void;
}

export const AdditionalBenefits: React.FC<AdditionalBenefitsProps> = ({ cooling, stormwater, onShowReferences }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="additional-benefits">
      <div className="additional-benefits__container">
        <div
          className="additional-benefits__header"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <Zap size={48} className="additional-benefits__header-icon" />
          <div style={{ flex: 1 }}>
            <h2 className="additional-benefits__title">Additional Ecosystem Services</h2>
            <p className="additional-benefits__subtitle">
              Energy savings, cooling services, and stormwater management
            </p>
          </div>
          {isExpanded ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
        </div>

        {isExpanded && (
          <>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Context */}
        <div className="additional-benefits__context">
          <div className="additional-benefits__context-icon">🌆</div>
          <div className="additional-benefits__context-content">
            <strong>Urban Context:</strong> These "hidden" ecosystem services represent enormous economic
            value. The combined cooling and stormwater benefits of {formatCurrency(cooling.energySavingsINR + cooling.gridReliefValue + stormwater.totalEconomicValue)}
            annually demonstrate that urban trees are essential green infrastructure, providing services
            that would cost far more to replace with traditional built infrastructure.
          </div>
        </div>

        <BotanicalDivider style="leaf" spacing="medium" />

        {/* Cooling Services Section */}
        <div className="additional-benefits__section">
          <h3 className="additional-benefits__section-title">
            <Thermometer size={32} />
            Cooling & Energy Services
          </h3>

          <div className="additional-benefits__grid">
            <MetricDisplay
              value={cooling.totalCoolingRooms.toLocaleString('en-IN')}
              label="Room Cooling Capacity"
              icon={<Building size={32} />}
              comparison="Equivalent to 10 AC units per tree"
              color="blue"
              size="medium"
              delay={0.1}
            />

            <MetricDisplay
              value={cooling.energySavingsKWh.toLocaleString('en-IN')}
              unit="kWh"
              label="Annual Energy Savings"
              icon={<Zap size={32} />}
              comparison={formatCurrency(cooling.energySavingsINR)}
              color="gold"
              size="medium"
              delay={0.2}
            />

            <MetricDisplay
              value={cooling.peakLoadReductionKW.toFixed(1)}
              unit="kW"
              label="Peak Load Reduction"
              icon={<Thermometer size={32} />}
              comparison="During summer peak hours"
              color="red"
              size="medium"
              delay={0.3}
            />
          </div>

          <NatureCard className="additional-benefits__details">
            <h4 className="additional-benefits__details-title">How Trees Cool Cities</h4>
            <div className="additional-benefits__details-grid">
              <div className="additional-benefits__detail-item">
                <div className="additional-benefits__detail-icon">🌳</div>
                <div>
                  <div className="additional-benefits__detail-label">Direct Shading</div>
                  <div className="additional-benefits__detail-text">
                    Reduces building surface temperatures by blocking direct sunlight
                  </div>
                </div>
              </div>

              <div className="additional-benefits__detail-item">
                <div className="additional-benefits__detail-icon">💨</div>
                <div>
                  <div className="additional-benefits__detail-label">Evapotranspiration</div>
                  <div className="additional-benefits__detail-text">
                    Cools ambient air through moisture release from leaves
                  </div>
                </div>
              </div>

              <div className="additional-benefits__detail-item">
                <div className="additional-benefits__detail-icon">🌡️</div>
                <div>
                  <div className="additional-benefits__detail-label">Urban Heat Island Mitigation</div>
                  <div className="additional-benefits__detail-text">
                    Reduces local temperatures by 2-8°C compared to un-shaded areas
                  </div>
                </div>
              </div>
            </div>

            <div className="additional-benefits__economic-highlight">
              <strong>Total Cooling Value:</strong> {formatCurrency(cooling.energySavingsINR + cooling.gridReliefValue)}{' '}annually
              <span className="additional-benefits__economic-note">
                (Includes energy savings + grid relief during peak demand)
              </span>
            </div>
          </NatureCard>
        </div>

        <BotanicalDivider style="leaf" spacing="large" emoji="💧" />

        {/* Stormwater Management Section */}
        <div className="additional-benefits__section">
          <h3 className="additional-benefits__section-title">
            <Droplet size={32} />
            Stormwater Management
          </h3>

          <div className="additional-benefits__grid">
            <MetricDisplay
              value={formatIndianNumber(stormwater.annualInterceptionLiters)}
              label="Liters Intercepted/Year"
              icon={<Droplet size={32} />}
              comparison={`${stormwater.annualInterceptionCubicMeters.toFixed(0)} cubic meters`}
              color="blue"
              size="medium"
              delay={0.1}
            />

            <MetricDisplay
              value={formatCurrency(stormwater.floodReductionValue)}
              label="Flood Reduction Value"
              icon={<Droplet size={32} />}
              comparison="Reduced runoff volume"
              color="forest"
              size="medium"
              delay={0.2}
            />

            <MetricDisplay
              value={formatCurrency(stormwater.totalEconomicValue)}
              label="Total Stormwater Value"
              icon={<Zap size={32} />}
              comparison="Annual infrastructure savings"
              color="gold"
              size="medium"
              delay={0.3}
            />
          </div>

          <NatureCard className="additional-benefits__details">
            <h4 className="additional-benefits__details-title">Stormwater Benefits</h4>
            <div className="additional-benefits__details-grid">
              <div className="additional-benefits__detail-item">
                <div className="additional-benefits__detail-icon">🌧️</div>
                <div>
                  <div className="additional-benefits__detail-label">Canopy Interception</div>
                  <div className="additional-benefits__detail-text">
                    Average tree intercepts 6,000 liters of rainfall annually
                  </div>
                </div>
              </div>

              <div className="additional-benefits__detail-item">
                <div className="additional-benefits__detail-icon">💧</div>
                <div>
                  <div className="additional-benefits__detail-label">Water Quality Improvement</div>
                  <div className="additional-benefits__detail-text">
                    Filters pollutants and sediment before reaching water bodies
                  </div>
                </div>
              </div>

              <div className="additional-benefits__detail-item">
                <div className="additional-benefits__detail-icon">🏗️</div>
                <div>
                  <div className="additional-benefits__detail-label">Infrastructure Savings</div>
                  <div className="additional-benefits__detail-text">
                    Reduces need for expensive gray infrastructure (storm drains, retention ponds)
                  </div>
                </div>
              </div>
            </div>

            <div className="additional-benefits__breakdown">
              <div className="additional-benefits__breakdown-title">Economic Breakdown:</div>
              <div className="additional-benefits__breakdown-items">
                <div className="additional-benefits__breakdown-item">
                  <span>Water Quality Treatment:</span>
                  <strong>{formatCurrency(stormwater.waterQualityValue)}</strong>
                </div>
                <div className="additional-benefits__breakdown-item">
                  <span>Flood Risk Reduction:</span>
                  <strong>{formatCurrency(stormwater.floodReductionValue)}</strong>
                </div>
                <div className="additional-benefits__breakdown-item">
                  <span>Infrastructure Savings:</span>
                  <strong>{formatCurrency(stormwater.infrastructureSavings)}</strong>
                </div>
              </div>
            </div>
          </NatureCard>
        </div>

        {/* References Button - Hidden for now */}
        {false && onShowReferences && (
          <div className="additional-benefits__references">
            <button
              className="additional-benefits__references-btn"
              onClick={onShowReferences}
            >
              <BookOpen size={20} />
              <span>View Scientific References & Methodology</span>
            </button>
            <p className="additional-benefits__references-note">
              Access complete calculations, data sources, and academic citations
            </p>
          </div>
        )}
        </>
        )}
      </div>
    </section>
  );
};

export default AdditionalBenefits;
